import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useChatroom } from '../../contexts/ChatroomContext';
import { VoiceInput } from '../VoiceInput/VoiceInput';
import { MentionAutocomplete, filterMentionParticipants } from './MentionAutocomplete';
import { restorePunctuation } from '../../api/punctuation';
import './MessageInput.css';

const PAUSE_PREFIX = '<automated>This human user paused the conversation to type this message.</automated>\n';

/**
 * Check if the @ at the given position should trigger autocomplete.
 * Only triggers if @ is at start or after a space/newline (word boundary).
 */
function shouldTriggerMention(content, atPosition) {
  if (atPosition === 0) return true;
  const charBefore = content[atPosition - 1];
  return charBefore === ' ' || charBefore === '\n';
}

export function MessageInput() {
  const {
    activeChannelId,
    activeChannel,
    isConnected,
    sendMessage,
    sendTyping,
    pauseChannel,
    unpauseChannel,
    pausedChannels,
    participants,
  } = useChatroom();
  const [content, setContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isPauseLoading, setIsPauseLoading] = useState(false);
  const textareaRef = useRef(null);
  const baseContentRef = useRef(''); // Content before voice input started
  const isVoiceActiveRef = useRef(false); // Track if voice input is active

  // Mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [mentionStartPosition, setMentionStartPosition] = useState(null);

  // Get participants for the active channel
  const channelParticipants = activeChannelId ? (participants[activeChannelId] || []) : [];

  // Check if LLM-to-LLM triggering is enabled for this channel
  const triggerOnLlmMessage = activeChannel?.settings?.trigger_on_llm_message ?? false;
  // Check if this channel is currently paused
  const isPaused = activeChannelId ? (pausedChannels[activeChannelId] ?? false) : false;

  const handleChange = useCallback((e) => {
    const newContent = e.target.value;
    const cursorPos = e.target.selectionStart;
    setContent(newContent);

    // Reset voice refs when user manually edits
    // This ensures editing works normally after voice input
    if (isVoiceActiveRef.current) {
      isVoiceActiveRef.current = false;
      baseContentRef.current = '';
    }

    // Check for @ mention trigger
    // Look for @ before cursor position
    if (cursorPos > 0) {
      // Find the most recent @ before cursor
      let atPos = -1;
      for (let i = cursorPos - 1; i >= 0; i--) {
        const char = newContent[i];
        if (char === '@') {
          atPos = i;
          break;
        }
        // Stop if we hit a space (word boundary) without finding @
        if (char === ' ' || char === '\n') {
          break;
        }
      }

      if (atPos !== -1 && shouldTriggerMention(newContent, atPos)) {
        // Extract filter text (everything between @ and cursor)
        const filterText = newContent.substring(atPos + 1, cursorPos);
        // Only show if no spaces in filter (single word)
        if (!filterText.includes(' ')) {
          setShowMentionAutocomplete(true);
          setMentionFilter(filterText);
          setMentionStartPosition(atPos);
          setMentionSelectedIndex(0);
        } else {
          setShowMentionAutocomplete(false);
        }
      } else {
        setShowMentionAutocomplete(false);
      }
    } else {
      setShowMentionAutocomplete(false);
    }

    // Send typing indicator
    if (!isTyping && newContent) {
      setIsTyping(true);
      sendTyping(true);
    } else if (isTyping && !newContent) {
      setIsTyping(false);
      sendTyping(false);
    }
  }, [isTyping, sendTyping]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!content.trim()) return;

    // If paused, unpause first so the message triggers a bid cycle
    let messageContent = content;

    if (isPaused) {
      messageContent = PAUSE_PREFIX + content;
      // Unpause the channel BEFORE sending so the message triggers a bid cycle
      await unpauseChannel(activeChannelId);
    }

    // skipBidTrigger is always false - we want normal bid triggering
    sendMessage(messageContent, { skipBidTrigger: false });
    setContent('');

    // Stop typing indicator
    if (isTyping) {
      setIsTyping(false);
      sendTyping(false);
    }
  }, [content, isTyping, isPaused, activeChannelId, sendMessage, sendTyping, unpauseChannel]);

  const handlePause = useCallback(async () => {
    if (activeChannelId && !isPauseLoading) {
      setIsPauseLoading(true);
      try {
        await pauseChannel(activeChannelId);
      } finally {
        setIsPauseLoading(false);
      }
    }
  }, [activeChannelId, pauseChannel, isPauseLoading]);

  // Handle mention selection
  const handleMentionSelect = useCallback((participant) => {
    if (mentionStartPosition === null) return;

    // Get current cursor position
    const cursorPos = textareaRef.current?.selectionStart || content.length;

    // Build the mention in markdown link format: @[Name](participant:uuid)
    const mentionText = `@[${participant.name}](participant:${participant.id}) `;

    // Replace @filter with the mention
    const before = content.substring(0, mentionStartPosition);
    const after = content.substring(cursorPos);
    const newContent = before + mentionText + after;

    setContent(newContent);
    setShowMentionAutocomplete(false);
    setMentionFilter('');
    setMentionStartPosition(null);

    // Focus textarea and set cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = mentionStartPosition + mentionText.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [content, mentionStartPosition]);

  const handleKeyDown = useCallback((e) => {
    // Handle mention autocomplete keyboard navigation
    if (showMentionAutocomplete) {
      // Use shared filtering function for consistency with MentionAutocomplete
      const filtered = filterMentionParticipants(channelParticipants, mentionFilter);
      const filteredCount = filtered.length;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex((prev) =>
          prev < filteredCount - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCount - 1
        );
        return;
      }

      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filtered[mentionSelectedIndex]) {
          handleMentionSelect(filtered[mentionSelectedIndex]);
        }
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionAutocomplete(false);
        return;
      }
    }

    // Enter without Shift sends the message
    // Shift+Enter inserts a newline (default textarea behavior)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [
    showMentionAutocomplete,
    mentionFilter,
    channelParticipants,
    mentionSelectedIndex,
    handleMentionSelect,
    handleSubmit,
  ]);

  const handleVoiceTranscript = useCallback(async (transcript, isListening) => {
    if (isListening) {
      // Interim results - show live preview
      // On first interim result, save the base content
      if (!isVoiceActiveRef.current) {
        isVoiceActiveRef.current = true;
        // Capture current content at the moment voice starts
        setContent(current => {
          baseContentRef.current = current;
          return current;
        });
      }

      // Show base content + live transcript
      // Replace [PAUSE] markers with periods for cleaner preview
      const displayTranscript = transcript.replace(/\s*\[PAUSE\]\s*/g, '. ');
      setContent(() => {
        const baseContent = baseContentRef.current;
        const separator = baseContent.trim() ? ' ' : '';
        return baseContent + separator + displayTranscript;
      });
    } else {
      // Final result - add punctuation and commit
      const baseContent = baseContentRef.current;
      const separator = baseContent.trim() ? ' ' : '';
      const rawText = baseContent + separator + transcript;

      // Call punctuation API
      const punctuatedText = await restorePunctuation(transcript);

      // Set final content with punctuation
      setContent(baseContent + separator + punctuatedText);

      // Reset refs after a small delay to ensure state has updated
      setTimeout(() => {
        baseContentRef.current = '';
        isVoiceActiveRef.current = false;
      }, 0);

      // Focus back on textarea after transcription
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, []);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, [content]);

  if (!activeChannelId) return null;

  const isDisabled = !content.trim() || !isConnected;

  return (
    <form className="message-input" onSubmit={handleSubmit}>
      <div className="message-input-wrapper">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={isConnected ? "Type a message... (@ to mention)" : "Connecting..."}
          autoFocus
          disabled={!isConnected}
          rows={1}
        />
        {showMentionAutocomplete && (
          <MentionAutocomplete
            participants={channelParticipants}
            filter={mentionFilter}
            selectedIndex={mentionSelectedIndex}
            onSelect={handleMentionSelect}
            position={{ bottom: '100%', left: 0 }}
          />
        )}
      </div>
      <div className="message-input-buttons">
        <VoiceInput
          onTranscript={handleVoiceTranscript}
          disabled={!isConnected}
        />
        {triggerOnLlmMessage && (
          <button
            type="button"
            className={`pause-button ${isPaused ? 'paused' : ''}`}
            onClick={handlePause}
            disabled={isPaused || !isConnected || isPauseLoading}
            title={isPaused ? "Paused - your next message will skip LLM responses" : "Pause to type without triggering LLM responses"}
          >
            {isPauseLoading ? 'Pausing...' : isPaused ? 'Paused' : 'Pause'}
          </button>
        )}
        <button type="submit" disabled={isDisabled}>
          Send
        </button>
      </div>
    </form>
  );
}
